import { assertNever } from "../../../util/assertNever";
import { CommonColumnOperators, DateColumnOperators } from "../enum.filterOperators";
import { DateFilterType, GenericTableFilterType } from "../type.genericTableQuery";
import type { FilterMapper } from "./interface.filterMapper";

export class DateFilterMapper implements FilterMapper {
  toQuery({ filter }: DateFilterType): undefined | string {
    const option = filter.option;
    switch (option) {
      case DateColumnOperators.BETWEEN: {
        if (!Array.isArray(filter.value)) {
          throw new Error("between filter requires an array of two dates as value");
        }
        const lowerBound = filter.value[0];
        const upperBound = filter.value[1];
        return `${DateColumnOperators.BETWEEN}$-$${lowerBound}$-$${upperBound}`;
      }
      case DateColumnOperators.BEFORE: {
        const beforeDate = filter.value;
        return `${DateColumnOperators.BEFORE}$-$${beforeDate}`;
      }
      case DateColumnOperators.AFTER: {
        const afterDate = filter.value;
        return `${DateColumnOperators.AFTER}$-$${afterDate}`;
      }
      case DateColumnOperators.EXACT: {
        const exactDate = filter.value;
        return `${DateColumnOperators.EXACT}$-$${exactDate}`;
      }
      case CommonColumnOperators.VALUE_EXISTS: {
        const missingValue = filter.value;
        return `${CommonColumnOperators.VALUE_EXISTS}$-$${missingValue}`;
      }
      default:
        assertNever(option);
    }
  }

  fromQuery(value: string): DateFilterType {
    const [columnOperator, firstValue, secondValue] = value.split("$-$");
    if (!columnOperator || !firstValue) {
      throw new Error("missing filter option or value");
    }
    const option = this.parseDateColumnOperator(columnOperator);

    switch (option) {
      case DateColumnOperators.BETWEEN: {
        if (!secondValue) {
          throw new Error("two bounds required for BETWEEN");
        }
        return {
          type: GenericTableFilterType.DATE,
          filter: { option, value: [firstValue, secondValue] },
        };
      }
      case CommonColumnOperators.VALUE_EXISTS: {
        return {
          type: GenericTableFilterType.DATE,
          filter: { option, value: firstValue === "true" },
        };
      }
      case DateColumnOperators.BEFORE:
      case DateColumnOperators.AFTER:
      case DateColumnOperators.EXACT: {
        return {
          type: GenericTableFilterType.DATE,
          filter: { option, value: firstValue },
        };
      }
      default:
        assertNever(option);
    }
  }

  private parseDateColumnOperator(option: string): CommonColumnOperators | DateColumnOperators {
    const allOptions = [...Object.values(DateColumnOperators), ...Object.values(CommonColumnOperators)];
    if (allOptions.includes(option as CommonColumnOperators | DateColumnOperators)) {
      return option as CommonColumnOperators | DateColumnOperators;
    }
    throw new Error(`Invalid date filter option: ${option}`);
  }
}
